'use client';

import { useState } from 'react';
import Link from 'next/link';
import { clsx } from 'clsx';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { Job } from '@/sections/careers/greenhouse-types';

export function JobList({ jobsByDepartment }: { jobsByDepartment: Record<string, Job[]> }) {
  const [openDepartments, setOpenDepartments] = useState<Record<string, boolean>>(
    Object.keys(jobsByDepartment).reduce((acc, dept) => ({ ...acc, [dept]: true }), {}),
  );

  const toggleDepartment = (department: string) => {
    setOpenDepartments(prev => ({
      ...prev,
      [department]: !prev[department],
    }));
  };

  return (
    <div className="flex flex-col gap-12 md:gap-14">
      {Object.entries(jobsByDepartment).map(([department, jobs]) => (
        <div key={department}>
          <button className="group mb-6 flex w-full items-center gap-4" onClick={() => toggleDepartment(department)}>
            <h4 className="text-h6">{department}</h4>
            <div className="mr-2 size-6 md:mr-4 lg:mr-6">
              {openDepartments[department] ? (
                <ChevronUpIcon className="size-6 group-hover:text-gradle-blue" />
              ) : (
                <ChevronDownIcon className="size-6 group-hover:text-gradle-blue" />
              )}
            </div>
          </button>

          <div
            className={clsx(
              'grid transition-all duration-300 ease-out',
              openDepartments[department] ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
            )}
          >
            <div className="space-y-6 overflow-hidden">
              {jobs.map(job => (
                <JobCard key={job.id} job={job} />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function JobCard({ job }: { job: Job }) {
  return (
    <Link key={job.id} href={`/careers/open-roles/${job.id}`} className="group block w-full rounded-2xl bg-white p-6">
      <div className="flex items-center justify-between">
        <div className="space-y-3">
          <p className="text-body-bold text-gray-1">{job.title}</p>
          <p className="text-gray-600 grid w-fit place-content-center rounded-4xl bg-[#EBF7FF] px-3 py-1 text-footnote-lg text-gray-2">
            {job.location.name}
          </p>
        </div>
        <div className="flex space-x-4">
          <div className="text-blue-600 hover:underline">
            <ArrowRightIcon className="size-6 duration-300 group-hover:text-gradle-blue" />
          </div>
        </div>
      </div>
    </Link>
  );
}
